import { parse } from 'qs';
import type ItineraryPriceLoader from './ItineraryPriceLoader';
import type {
    LocationGroup,
    UrlStringParams,
    PaxModel,
    dayChangerFunc,
} from './types';
import { type ItineraryPathData, type ItineraryParams } from '@utils/itinerary';
import {
    PaxType,
    SearchMode,
    type Location,
    type ReserveInfo,
    type DateSpanObject,
    type PaxCount,
    type PaxCapacityInfo,
} from '@utils/types';
import type { AnalyticsAttribute } from '@store/analytics';

export default abstract class ItinerarySearchForm<
    TLocation extends Location<TLocation> = Location,
    TPathData extends ItineraryPathData = ItineraryPathData,
> {
    //#region pax-count
    abstract initPaxCounts: PaxCount[];
    // final
    setPaxTypeCount(data: { type: PaxType; count: any }) {
        const count = parseInt(data.count);
        const paxCountInfo = this.capacityInfo[data.type];
        const paxCount = this.initPaxCounts[data.type];
        if (
            !paxCountInfo ||
            count < paxCountInfo.min ||
            count > paxCountInfo.max
        ) {
            return;
        }
        if (paxCountInfo && paxCount) {
            paxCount.count = count;
        }
    }

    get paxMaxSizeMessage(): undefined | string {
        return undefined;
    }

    get capacityInfo(): PaxCapacityInfo {
        return {};
    }

    // bundle only
    get searchPaxModel(): PaxModel[] {
        return [
            {
                paxList: this.initPaxCounts,
            },
        ];
    }

    // bundle only
    get adultCount() {
        const count = this.initPaxCounts.find(
            count => count.code === PaxType.Adult
        );
        return count?.count || 1;
    }

    get childCount() {
        const count = this.initPaxCounts.find(
            count => count.code === PaxType.Child
        );
        return count?.count || 0;
    }

    get infantCount() {
        const count = this.initPaxCounts.find(
            count => count.code === PaxType.Infant
        );
        return count?.count || 0;
    }

    // bundle
    // train
    // bus
    // flight
    updatePaxCount(params: { type: PaxType; count: number }) {
        const initialPaxCount = this.initPaxCounts.find(
            pax => pax.code === params.type
        );
        const paxCount = this.capacityInfo[params.type];
        if (
            !paxCount ||
            !initialPaxCount ||
            params.count > paxCount.max ||
            params.count < paxCount.min
        ) {
            throw new Error('بیش از ظرفیت مجاز');
        }
        initialPaxCount.count = params.count;
    }

    // bundle only
    updatePaxCountAndValidate(params: {
        type: PaxType;
        count: number;
        roomIndex?: number;
    }) {
        this.updatePaxCount(params);
        // reset state if counts are out of bound
        this.initPaxCounts.forEach(paxCount => {
            const capacity = this.capacityInfo[paxCount.code];
            const newCount = Math.min(
                Math.max(paxCount.count, capacity?.min || 0),
                capacity?.max || 0
            );
            if (paxCount.count !== newCount) {
                this.updatePaxCount({ type: paxCount.code, count: newCount });
            }
        });
    }
    //#endregion

    //#region prices
    // trip
    priceLoader: ItineraryPriceLoader | undefined = undefined;
    // trip
    abstract get loadPriceEnabled(): boolean;
    // trip
    abstract loadPrices(payload?: { until: Date }): Promise<void>;

    // final
    get prices() {
        return this.priceLoader?.labeledPrices;
    }

    // final
    get loadingPrice(): boolean {
        return !!this.priceLoader?.isLoading;
    }

    protected departureDate: Date | null = null;

    //#endregion

    abstract get reserveInfo(): ReserveInfo;

    // shallow
    abstract saveSearch(): void;

    // shallow
    abstract get searchReady(): boolean;

    // shallow
    abstract loadLastSearch(setSearchMode: boolean): void;

    // final
    isLastSearchLoaded = false;
    // ship only
    searchMode: SearchMode = SearchMode.Domestic;
    // final
    get isDomestic() {
        return this.searchMode === SearchMode.Domestic;
    }

    // final
    get isForeign() {
        return this.searchMode === SearchMode.Foreign;
    }

    abstract parsePath(
        pathData: TPathData
    ): Promise<ItineraryParams> | ItineraryParams;

    abstract verifyViewPath(path: string, pathData: TPathData): Promise<void>;

    abstract loadViewUrlParams(params: ItineraryParams): Promise<void>;

    // shallow
    abstract get searchAttributes(): AnalyticsAttribute;

    // shallow
    abstract get itinerarySearchDates(): DateSpanObject | null;
    // trip
    abstract setItinerarySearchDates(val: DateSpanObject | null): void;
    // final
    isLoadingLocations = false;
    // trip
    abstract get filteredDestinations(): LocationGroup[];
    // trip
    abstract get destination(): TLocation | null;
    // trip
    abstract setDestination(location: TLocation | null): void;
    // hotel
    abstract searchLocations(
        searchKeyword: string,
        options?: Object
    ): Promise<void>;

    getLocationKeywords(location: TLocation): string[] {
        const keywords = [
            location.title,
            location.subtitle ?? '',
            location.countryName ?? '',
            location.code,
        ];
        if (location.subLocations?.length) {
            keywords.push(
                ...location.subLocations.flatMap(l =>
                    this.getLocationKeywords(l)
                )
            );
        }
        return keywords;
    }

    filterLocationsBaseOnToken(token: string, list: TLocation[]): TLocation[] {
        const searchList = list.map(location => ({
            ...location,
            ...searchPersianInStrings(token, location.keywords ?? [], true),
        }));
        return searchList
            .filter(l => l.matched)
            .sort((a, b) => {
                if (a.matchedPosition > b.matchedPosition) {
                    return 1;
                } else if (b.matchedPosition > a.matchedPosition) {
                    return -1;
                } else {
                    return 0;
                }
            });
    }

    // final
    findLocationInLocations(code: string, list: TLocation[]): TLocation | null {
        let location: TLocation | null = null;
        list.forEach(loc => {
            if (!location) {
                if (loc.code === code) {
                    location = loc;
                } else if (loc.subLocations?.length) {
                    location =
                        this.findLocationInLocations(code, loc.subLocations) ??
                        null;
                }
            }
        });
        return location;
    }

    // trip only
    async advancedSearch(_params: {
        token: string;
        target: 'origin' | 'destination';
    }) {
        await Promise.reject(new Error('Not implemented'));
    }

    // final
    get lastSearchKey(): string {
        return `search.${this.reserveInfo.type}`;
    }

    // trip
    abstract getSearchUrl(): Promise<string>;

    abstract get viewUrl(): string;
    abstract nextDay(callbackFunc: dayChangerFunc): void;
    abstract prevDay(callbackFunc: dayChangerFunc): void;
    abstract generateViewUrl(params: UrlStringParams<ItineraryParams>): string;
    // shallow
    abstract verifySearchForm(currentUrl: string): Promise<void>;
    // final
    duplicateViewPath(currentFullPath: string) {
        const viewPath = this.viewUrl;
        const [currentPath] = currentFullPath.split('#');
        const [cPath, cQuery] = currentPath.split('?');
        const [vPath, vQuery] = viewPath.split('?');
        if (cPath.replace(/\/+$/, '') !== vPath.replace(/\/+$/, '')) {
            return false;
        }
        const cQueries = parse(cQuery);
        const vQueries = parse(vQuery);
        const allKeys = Object.keys(vQueries);
        Object.keys(cQueries).forEach(key => {
            if (!allKeys.includes(key)) {
                allKeys.push(key);
            }
        });
        return allKeys.every(key => {
            const vValue = vQueries[key];
            const cValue = cQueries[key];
            if (
                (typeof vValue === 'string' &&
                    /\d{4}-\d{1,2}-\d{1,2}/.test(vValue as string)) ||
                (typeof cValue === 'string' &&
                    /\d{4}-\d{1,2}-\d{1,2}/.test(cValue as string))
            ) {
                const vDate = vValue ? parseDate(vValue as string) : new Date();
                const cDate = cValue ? parseDate(cValue as string) : new Date();
                return dayConfig.js(vDate).isSame(cDate, 'day');
            } else if (key === 'adultCount') {
                return Number(vValue || 1) === Number(cValue || 1);
            } else if (!isNaN(Number(vValue)) || !isNaN(Number(cValue))) {
                return Number(vValue || 0) === Number(cValue || 0);
            } else {
                return vValue === cValue;
            }
        });
    }

    toJSON() {
        return { ...this };
    }
}
